import { combineLatest, map } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  activeMultiselectOptionsCount,
  SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { connect } from '@ardoq/rxbeach';
import { ViewIds, FormattingRuleOperator } from '@ardoq/api-types';
import { LayoutOrientation } from '@ardoq/yfiles';
import { isTimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import { isViewpointModeDependencyMapSettingsControlsDependencies } from 'tabview/dependencyMap/viewpointMode/settingsControls';
import { isTruthy } from '@ardoq/common-helpers';
import { IconName } from '@ardoq/icons';
import { DropdownOptionType, isDropdownOption } from '@ardoq/dropdown-menu';
import {
  formatDateShort,
  parseDate,
  relativeDateDisplayLabel,
} from '@ardoq/date-time';
import { type Locale } from '@ardoq/locale';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { Box } from '@ardoq/layout';
import { findActiveDropdownOptionTagLabel } from '@ardoq/bubble-chart';
import { surface3DDisplayValues } from 'tabview/xyzCharts/displayValues';
import { viewModifiers$ } from '../viewModifiers$';
import { locale$ } from 'streams/locale$';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { ViewSettingsRow } from './ViewSettingsRow';
import { ViewSettingsSection } from './ViewSettingsSection';
import { BubbleChartViewSettingsRows } from 'tabview/bubbleChart/viewpointMode/BubbleChartViewSettingsRows';
import { toBubbleChartViewSettingsRows } from 'tabview/bubbleChart/viewpointMode/viewSettingsRowsByRole';
import type {
  BlockDiagramViewProperties,
  ModernizedBlockDiagramViewProperties,
} from 'tabview/blockDiagram/types';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { ExtractStreamShape } from 'tabview/types';
import { isFlexibleBlockDiagramViewProperties } from 'tabview/flexibleBlockDiagram/types';
import type { FlexibleBlockDiagramViewSettings } from 'tabview/flexibleBlockDiagram/types';
import {
  findActiveLinksAggregationTagLabel,
  isBlocksViewPropertiesWithViewModifiers,
} from 'tabview/blocks/viewModel$/viewModifiers';
import type { ComponentStyle } from 'tabview/assistantShared/buildStyleByModelId';
import type { ComponentMatrixViewProperties } from 'tabview/componentMatrix/types';
import type { ViewpointModeComponentMatrixViewProperties } from 'tabview/componentMatrix/componentMatrix2/types';
import type {
  HierarchicalTreemapViewpointViewProperties,
  HierarchicalViewpointViewProperties,
} from 'tabview/hierarchicalTreemap/viewpoint/types';
import {
  maxDepthDisplayValue,
  sizeByFieldDisplayValue,
  tilingMethodDisplayValue,
} from 'tabview/hierarchicalTreemap/viewpoint/displayValues';
import { matchSelectedReferenceType } from '@ardoq/settings-bar';
import { formatRelativeDateValue as getRelativeDateLabel } from '@ardoq/perspectives';
import { TimelineDateRangeContent } from './timelineDateRange/TimelineDateRangeContent';
import { toTimelineDateRangeContentProps } from './timelineDateRange/timelineDateRangePropMappers';
import { getConditionOptions } from './timelineDateRange/consts';
import { isRelationshipsSettingsControlsDependencies } from 'tabview/relationsD3View/hierarchical/viewpointMode/settingsControls';

const getLayoutOrientationLabels = (
  i18nLocale: I18nLocale
): Record<LayoutOrientation, string> => ({
  [LayoutOrientation.LEFT_TO_RIGHT]: t(
    i18nLocale,
    'appLayout:viewPaneHeader.leftToRight',
    'Left to right'
  ),
  [LayoutOrientation.TOP_TO_BOTTOM]: t(
    i18nLocale,
    'appLayout:viewPaneHeader.topToBottom',
    'Top to bottom'
  ),
  [LayoutOrientation.RIGHT_TO_LEFT]: t(
    i18nLocale,
    'appLayout:viewPaneHeader.rightToLeft',
    'Right to left'
  ),
  [LayoutOrientation.BOTTOM_TO_TOP]: t(
    i18nLocale,
    'appLayout:viewPaneHeader.bottomToTop',
    'Bottom to top'
  ),
});

const formatRangeDate = (
  dateValue: string | Date | null,
  locale: Locale,
  i18nLocale: I18nLocale
): string => {
  if (!dateValue) return '';
  if (dateValue instanceof Date) return formatDateShort(dateValue, locale);
  return (
    relativeDateDisplayLabel(dateValue, i18nLocale) ||
    formatDateShort(parseDate(dateValue), locale)
  );
};

const getConditionLabel = (
  operator: FormattingRuleOperator,
  i18nLocale: I18nLocale
): string =>
  getConditionOptions(i18nLocale).find(option => option.value === operator)
    ?.label ?? '';

type DisplayValueGetter = (
  viewProperties: Record<string, any>,
  locale: Locale,
  i18nLocale: I18nLocale,
  viewModifier: SettingsConfig
) => string;

/**
 * The Component style tag, shared by the three views that carry the setting.
 * Takes the settings rather than the whole `viewProperties`, so each entry keeps
 * casting to its own view's type — a getter reading through `Record<string, any>`
 * would type `componentStyle` as `any` and quietly report Icon for every value if
 * the field were ever renamed or moved.
 */
const componentStyleDisplayValue = (
  { componentStyle }: { componentStyle: ComponentStyle },
  i18nLocale: I18nLocale
): string =>
  componentStyle === 'shape'
    ? t(i18nLocale, 'appLayout:viewPaneHeader.shape', 'Shape')
    : t(i18nLocale, 'appLayout:viewPaneHeader.icon', 'Icon');

/**
 * Build the row/column display tag for the Component Matrix view-settings
 * panel.
 *
 * The internal `viewSettings.selectedFieldNames{X,Y}` are field *names*
 * (e.g. `"service_level"`); the user-facing label is on
 * `viewSettingsState.listFields[].label` (e.g. `"Service Level"`). And
 * when a reference type is selected (instead of, or alongside, a list
 * field) the user-facing label is the traversed reference type's
 * `displayName`.
 */
const getAxisDisplayValue = (
  viewProperties:
    | ComponentMatrixViewProperties
    | ViewpointModeComponentMatrixViewProperties,
  axis: 'X' | 'Y'
): string => {
  const { viewSettings, viewSettingsState } = viewProperties;
  if ('useParentAsX' in viewSettings) {
    const useParent =
      axis === 'X' ? viewSettings.useParentAsX : viewSettings.useParentAsY;
    if (useParent) return 'Parent';
  }
  const fieldNames =
    axis === 'X'
      ? viewSettings.selectedFieldNamesX
      : viewSettings.selectedFieldNamesY;
  const referenceType =
    axis === 'X' ? viewSettings.referenceTypeX : viewSettings.referenceTypeY;
  const fieldLabels = fieldNames
    .map(
      name =>
        viewSettingsState.listFields.find(field => field.name === name)?.label
    )
    .filter(isTruthy);
  const referenceLabel = referenceType
    ? ([
        ...viewSettingsState.traversedIncomingReferenceTypes,
        ...viewSettingsState.traversedOutgoingReferenceTypes,
      ].find(refType => matchSelectedReferenceType(refType, referenceType))
        ?.displayName ?? null)
    : null;
  const labels = [...fieldLabels, referenceLabel].filter(isTruthy);
  return labels.length ? labels.join(', ') : '-';
};

const layoutTypeDisplayValue = (
  { layoutType }: FlexibleBlockDiagramViewSettings,
  i18nLocale: I18nLocale
): string => {
  // `?? 'process'` as `toLayoutStyle` reads it, so the panel names what the diagram
  // draws. `sanitizeSingleViewSettings` fills an absent key from the defaults, so this
  // is defence rather than a state the pipeline delivers.
  switch (layoutType ?? 'process') {
    case 'process':
      return t(
        i18nLocale,
        'flexibleBlockDiagram:layoutType.process',
        'Process'
      );
    case 'compact':
      return t(
        i18nLocale,
        'flexibleBlockDiagram:layoutType.compact',
        'Compact'
      );
    case 'seriesparallel':
      return t(
        i18nLocale,
        'flexibleBlockDiagram:layoutType.seriesparallel',
        'Series-parallel'
      );
    case 'grid':
      return t(i18nLocale, 'flexibleBlockDiagram:layoutType.grid', 'Grid');
    default:
      return '-';
  }
};

const referenceLinesDisplayValue = (
  { hideReferences, separateReferences }: FlexibleBlockDiagramViewSettings,
  i18nLocale: I18nLocale
): string => {
  if (hideReferences) {
    return t(i18nLocale, 'appLayout:viewPaneHeader.hide', 'Hide');
  }
  if (separateReferences) {
    return t(i18nLocale, 'appLayout:viewPaneHeader.separate', 'Separate');
  }
  return t(i18nLocale, 'appLayout:viewPaneHeader.combine', 'Combine');
};

/**
 * Shared by the three viewpoint-mode hierarchical views. Only the treemap tiles, so only its
 * modifiers include the row `tilingMethod` names — the other two never reach that getter.
 *
 * `sizeByFieldName` reads the `sizing` the hierarchy was summed with rather than the setting it
 * came from. `toSizing` refuses a persisted name the view no longer offers and a confidential
 * field it offers but cannot read, and asking the raw setting again here named a field beside a
 * picture that was counting components.
 */
const hierarchicalViewDisplayValues: Record<string, DisplayValueGetter> = {
  sizeByFieldName: (viewProperties, _locale, i18nLocale) => {
    const { sizing, numericFields } =
      viewProperties as HierarchicalViewpointViewProperties;
    return sizeByFieldDisplayValue(sizing, numericFields, i18nLocale);
  },
  tilingMethod: (viewProperties, _locale, i18nLocale) =>
    tilingMethodDisplayValue(
      (viewProperties as HierarchicalTreemapViewpointViewProperties)
        .viewSettings.tilingMethod,
      i18nLocale
    ),
  maxDepthContainer: (viewProperties, _locale, i18nLocale) =>
    maxDepthDisplayValue(
      (viewProperties as HierarchicalViewpointViewProperties).viewSettings
        .maxDepth,
      i18nLocale
    ),
};

const bubbleChartDisplayValue: DisplayValueGetter = (
  viewProperties,
  _locale,
  i18nLocale,
  viewModifier
) => {
  const label = findActiveDropdownOptionTagLabel(viewModifier);

  if (label !== '-' || !viewProperties.hasReferenceBubbleChartFeature) {
    return label;
  }

  return t(i18nLocale, 'appLayout:noneLabel', 'None');
};

const BUBBLE_CHART_TAGGED_VIEW_MODIFIER_IDS = [
  'selectedComponentTypes',
  'valueSource',
  'selectedFieldNameX',
  'selectedFieldNameY',
  'selectedFieldNameRadius',
  'showBackground',
];

/**
 * The single-select rows on the gridded and point charts, in the order the settings bar shows them.
 *
 * The appearance dropdowns are absent on purpose: each is a column of toggles rather than a choice,
 * so there is no one value to name beside it.
 */
const CONTOUR_TAGGED_VIEW_MODIFIER_IDS = [
  'contourFieldX',
  'contourFieldY',
  'contourFieldZ',
  'contourInterpolation',
  'contourAggregation',
  'contourResolution',
];

const NETWORK_3D_TAGGED_VIEW_MODIFIER_IDS = [
  'network3dLayout',
  'network3dNodeSize',
];

const SPIDER_2_TAGGED_VIEW_MODIFIER_IDS = ['spider2MarkType'];

const POLAR_TAGGED_VIEW_MODIFIER_IDS = [
  'polarFieldAngle',
  'polarFieldRadius',
  'polarFieldSeries',
  'polarMarkType',
];

const POINTS_3D_TAGGED_VIEW_MODIFIER_IDS = [
  'points3dFieldX',
  'points3dFieldY',
  'points3dFieldZ',
  /* Only the bubble publishes this one. A getter for a row the view never shows is never called,
     so both charts can share the list. */
  'points3dFieldRadius',
];

/** Names each row from the config that row is rendered from. */
const tagsFromOwnModifier = (
  modifierIds: string[]
): Record<string, DisplayValueGetter> =>
  Object.fromEntries(
    modifierIds.map(id => [
      id,
      ((_viewProperties, _locale, _i18nLocale, viewModifier) =>
        findActiveDropdownOptionTagLabel(
          viewModifier
        )) satisfies DisplayValueGetter,
    ])
  );

export const displayValueGetters: Partial<
  Record<ViewIds, Record<string, DisplayValueGetter>>
> = {
  [ViewIds.HIERARCHICAL_TREEMAP]: hierarchicalViewDisplayValues,
  [ViewIds.HIERARCHICAL_PACKING]: hierarchicalViewDisplayValues,
  [ViewIds.SUNBURST]: hierarchicalViewDisplayValues,
  [ViewIds.BLOCK_DIAGRAM]: {
    layoutDirection: (viewProperties, _locale, i18nLocale) =>
      getLayoutOrientationLabels(i18nLocale)[
        (viewProperties as BlockDiagramViewProperties).viewSettings
          .layoutOrientation
      ],
    separateReferences: (viewProperties, _locale, i18nLocale) =>
      (viewProperties as BlockDiagramViewProperties).viewSettings
        .separateReferences
        ? t(i18nLocale, 'appLayout:viewPaneHeader.separate', 'Separate')
        : t(i18nLocale, 'appLayout:viewPaneHeader.combine', 'Combine'),
  },
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: {
    layoutDirection: (viewProperties, _locale, i18nLocale) =>
      getLayoutOrientationLabels(i18nLocale)[
        (viewProperties as ModernizedBlockDiagramViewProperties).viewSettings
          .layoutOrientation
      ],
    separateReferences: (viewProperties, _locale, i18nLocale) =>
      (viewProperties as ModernizedBlockDiagramViewProperties).viewSettings
        .separateReferences
        ? t(i18nLocale, 'appLayout:viewPaneHeader.separate', 'Separate')
        : t(i18nLocale, 'appLayout:viewPaneHeader.combine', 'Combine'),
    componentStyle: (viewProperties, _locale, i18nLocale) =>
      componentStyleDisplayValue(
        (viewProperties as ModernizedBlockDiagramViewProperties).viewSettings,
        i18nLocale
      ),
  },
  [ViewIds.DEPENDENCY_MAP_2]: {
    layoutDirection: (viewProperties, _locale, i18nLocale) => {
      if (
        !isViewpointModeDependencyMapSettingsControlsDependencies(
          viewProperties
        )
      ) {
        return '';
      }

      return viewProperties.viewSettings.showVertical
        ? t(i18nLocale, 'appLayout:viewPaneHeader.vertical', 'Vertical')
        : t(i18nLocale, 'appLayout:viewPaneHeader.horizontal', 'Horizontal');
    },
  },
  [ViewIds.RELATIONSHIPS_3]: {
    bundleRelationships: (viewProperties, _locale, i18nLocale) => {
      if (!isRelationshipsSettingsControlsDependencies(viewProperties)) {
        return '';
      }

      return viewProperties.viewSettings.bundleRelationships
        ? t(i18nLocale, 'appLayout:viewPaneHeader.combine', 'Combine')
        : t(i18nLocale, 'appLayout:viewPaneHeader.separate', 'Separate');
    },
  },
  [ViewIds.TIMELINE]: {
    phasesSubLevel: viewProperties => {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        return '';
      }

      const { phaseFieldOptions } = viewProperties;

      return activeMultiselectOptionsCount(phaseFieldOptions);
    },
    milestonesSubLevel: viewProperties => {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        return '';
      }

      const { milestoneFieldOptions } = viewProperties;

      return activeMultiselectOptionsCount(milestoneFieldOptions);
    },
    dateRangeSelection: (viewProperties, locale, i18nLocale) => {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        return '';
      }

      const { domainRange } = viewProperties.viewSettings;

      if (!domainRange) {
        return '';
      }

      if (!Array.isArray(domainRange)) {
        return getRelativeDateLabel(domainRange);
      }

      const [rangeStart, rangeEnd] = domainRange;

      if (rangeStart === null && rangeEnd !== null) {
        return `${getConditionLabel(FormattingRuleOperator.DATE_ON_OR_BEFORE, i18nLocale)} ${formatRangeDate(rangeEnd, locale, i18nLocale)}`;
      }
      if (rangeStart !== null && rangeEnd === null) {
        return `${getConditionLabel(FormattingRuleOperator.DATE_ON_OR_AFTER, i18nLocale)} ${formatRangeDate(rangeStart, locale, i18nLocale)}`;
      }

      return `${formatRangeDate(rangeStart, locale, i18nLocale)} - ${formatRangeDate(rangeEnd, locale, i18nLocale)}`;
    },
    gridPrecisionSelection: viewProperties => {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        return '';
      }

      const { gridPrecisionOptionsData } = viewProperties;

      const validOptions =
        gridPrecisionOptionsData.options.filter(isDropdownOption);

      const activeOption = validOptions.find(option => option.isActive);

      return activeOption?.label ?? '';
    },
  },
  /* Every view that publishes modifiers needs an entry, and the map is a `Partial<Record<…>>`,
     so nothing asks. Without one every row renders `displayValue = null` - on a view whose whole
     configuration is three field picks, the panel would list three rows and name none of them. */
  [ViewIds.SURFACE_3D]: surface3DDisplayValues,
  /* Read off the row's own config, the way the Bubble chart's entries are, rather than rebuilt
     from the view properties. `ViewSettingsRow` is handed the same `SettingsConfig` object, so the
     tag and the tick inside the dropdown it opens cannot disagree - they are one object read once
     rather than two derivations of it. */
  [ViewIds.CONTOUR]: tagsFromOwnModifier(CONTOUR_TAGGED_VIEW_MODIFIER_IDS),
  [ViewIds.SCATTER_3D]: tagsFromOwnModifier(POINTS_3D_TAGGED_VIEW_MODIFIER_IDS),
  [ViewIds.BUBBLE_3D]: tagsFromOwnModifier(POINTS_3D_TAGGED_VIEW_MODIFIER_IDS),
  [ViewIds.NETWORK_3D]: tagsFromOwnModifier(
    NETWORK_3D_TAGGED_VIEW_MODIFIER_IDS
  ),
  [ViewIds.SPIDER_2]: tagsFromOwnModifier(SPIDER_2_TAGGED_VIEW_MODIFIER_IDS),
  [ViewIds.POLAR]: tagsFromOwnModifier(POLAR_TAGGED_VIEW_MODIFIER_IDS),
  [ViewIds.BUBBLE]: Object.fromEntries(
    BUBBLE_CHART_TAGGED_VIEW_MODIFIER_IDS.map(id => [
      id,
      bubbleChartDisplayValue,
    ])
  ),
  [ViewIds.COMPONENT_MATRIX_2]: {
    yFields: viewProperties =>
      getAxisDisplayValue(
        viewProperties as ViewpointModeComponentMatrixViewProperties,
        'Y'
      ),
    xFields: viewProperties =>
      getAxisDisplayValue(
        viewProperties as ViewpointModeComponentMatrixViewProperties,
        'X'
      ),
    componentTypeSelector: (viewProperties, _locale, i18nLocale) => {
      const { selectedComponentTypes } = (
        viewProperties as ViewpointModeComponentMatrixViewProperties
      ).settableViewSettings;
      return selectedComponentTypes.length === 0
        ? t(i18nLocale, 'tabview:componentMatrix.leftMenu.allTypes', 'All')
        : selectedComponentTypes.join(', ');
    },
  },
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: {
    layoutType: (viewProperties, _locale, i18nLocale) => {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) return '';
      return layoutTypeDisplayValue(viewProperties.viewSettings, i18nLocale);
    },
    layoutDirection: (viewProperties, _locale, i18nLocale) => {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) return '';

      const labels = getLayoutOrientationLabels(i18nLocale);

      // Fall back on the lookup result, as the view model does for the same input: a
      // viewpoint persisted before ARD-33247 can carry an orientation that is not a key
      // here, and the diagram lays out left to right for it
      return (
        labels[viewProperties.viewSettings.layoutOrientation] ??
        labels[LayoutOrientation.LEFT_TO_RIGHT]
      );
    },
    referenceLines: (viewProperties, _locale, i18nLocale) => {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) return '';
      return referenceLinesDisplayValue(
        viewProperties.viewSettings,
        i18nLocale
      );
    },
    componentStyle: (viewProperties, _locale, i18nLocale) => {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) return '';
      return componentStyleDisplayValue(
        viewProperties.viewSettings,
        i18nLocale
      );
    },
  },
  [ViewIds.BLOCKS]: {
    linksAggregation: (viewProperties, _locale, i18nLocale) =>
      findActiveLinksAggregationTagLabel(viewProperties, i18nLocale),
    componentStyle: (viewProperties, _locale, i18nLocale) => {
      if (!isBlocksViewPropertiesWithViewModifiers(viewProperties)) return '';
      return componentStyleDisplayValue(
        viewProperties.viewSettings,
        i18nLocale
      );
    },
  },
};

const getEnhancedViewModifiers = (
  viewId: ViewIds,
  viewModifiers: SettingsConfig[],
  viewProperties: Record<string, any>,
  locale: Locale,
  i18nLocale: I18nLocale
): SettingsConfig[] => {
  if (
    !(
      viewId === ViewIds.TIMELINE &&
      isTimelineSettingsControlsDependencies(viewProperties)
    )
  ) {
    return viewModifiers;
  }

  const timelineViewSettings = viewProperties?.viewSettings || {};

  const {
    gridPrecisionOptionsData: {
      options: gridPrecisionOptions,
      value: gridPrecisionValue,
    },
    milestoneFieldOptions,
    phaseFieldOptions,
  } = viewProperties;

  const timelineDateRangeContentProps = toTimelineDateRangeContentProps({
    domainRange: timelineViewSettings.domainRange,
    onDomainRangeChange: value =>
      onViewSettingsUpdate(ViewIds.TIMELINE, { domainRange: value }, true),
    locale,
    i18nLocale,
    renderInPortal: true,
  });

  return [
    {
      type: SettingsType.DROPDOWN,
      id: 'dateRangeSelection',
      label: t(i18nLocale, 'appLayout:viewPaneHeader.dateRange', 'Date range'),
      iconName: IconName.CALENDAR,
      options: [
        {
          content: (
            <Box
              key="dateRangeSelection"
              padding="small"
              style={{ width: '380px' }}
            >
              <TimelineDateRangeContent {...timelineDateRangeContentProps} />
            </Box>
          ),
          type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
        },
      ],
      isKeepOpen: true,
    },
    gridPrecisionOptions && {
      id: 'gridPrecisionSelection',
      label: t(i18nLocale, 'appLayout:viewPaneHeader.layout', 'Layout'),
      iconName: IconName.CALENDAR,
      type: SettingsType.DROPDOWN,
      options: gridPrecisionOptions,
      currentValue: gridPrecisionValue,
      isKeepOpen: true,
    },

    phaseFieldOptions && {
      id: 'phasesSubLevel',
      label: t(i18nLocale, 'appLayout:viewPaneHeader.phases', 'Phases'),
      iconName: IconName.FORMAT_LIST_BULLETED,
      type: SettingsType.DROPDOWN,
      options: phaseFieldOptions,
      isKeepOpen: true,
      dropdownSize: DropdownSize.M,
    },
    milestoneFieldOptions && {
      id: 'milestonesSubLevel',
      label: t(i18nLocale, 'appLayout:viewPaneHeader.milestones', 'Milestones'),
      iconName: IconName.RHOMBUS,
      type: SettingsType.DROPDOWN,
      options: milestoneFieldOptions,
      isKeepOpen: true,
      dropdownSize: DropdownSize.M,
    },
  ].filter(isTruthy);
};

const ViewOptionsViewSettings = ({
  mainViewId,
  viewModifiers,
  bubbleChartViewSettingsRows,
  viewProperties,
  locale,
  i18nLocale,
}: ExtractStreamShape<typeof viewProperties$>) =>
  Boolean(viewModifiers.length) && (
    <ViewSettingsSection
      title={
        viewIdToUserFriendlyName[mainViewId]
          ? `${viewIdToUserFriendlyName[mainViewId]} ${t(i18nLocale, 'appLayout:settingsSuffix', 'settings')}`
          : t(i18nLocale, 'appLayout:currentSettingsTitle', 'Current Settings')
      }
      ariaLabel={t(
        i18nLocale,
        'appLayout:viewPaneHeader.viewSettingsPanelAriaLabel',
        'View settings panel'
      )}
    >
      {bubbleChartViewSettingsRows ? (
        <BubbleChartViewSettingsRows
          rows={bubbleChartViewSettingsRows}
          viewId={mainViewId}
          getDisplayValue={viewModifier =>
            displayValueGetters[mainViewId]?.[viewModifier.id]?.(
              viewProperties,
              locale,
              i18nLocale,
              viewModifier
            ) ?? null
          }
        />
      ) : (
        viewModifiers.map(viewModifier => (
          <ViewSettingsRow
            key={viewModifier.id}
            viewId={mainViewId}
            viewModifier={viewModifier}
            displayValue={
              displayValueGetters[mainViewId]?.[viewModifier.id]?.(
                viewProperties,
                locale,
                i18nLocale,
                viewModifier
              ) ?? null
            }
          />
        ))
      )}
    </ViewSettingsSection>
  );

const viewProperties$ = combineLatest([
  viewModifiers$,
  locale$,
  i18nLocale$,
]).pipe(
  map(
    ([
      { mainViewId, viewModifiers, viewProperties, viewPropertiesViewId },
      locale,
      i18nLocale,
    ]) => ({
      mainViewId,
      viewProperties,
      locale,
      i18nLocale,
      viewModifiers: getEnhancedViewModifiers(
        viewPropertiesViewId,
        viewModifiers,
        viewProperties,
        locale,
        i18nLocale
      ),
      bubbleChartViewSettingsRows: toBubbleChartViewSettingsRows(
        viewPropertiesViewId,
        viewProperties,
        viewModifiers
      ),
    })
  )
);

const ConnectedViewOptionsViewSettings = connect(
  ViewOptionsViewSettings,
  viewProperties$
);
export { ConnectedViewOptionsViewSettings };
